/**
 * Nutrition — target + daily summary.
 */

import { useState, type FormEvent } from 'react';
import {
  useNutritionTarget,
  useCreateNutritionTarget,
  useNutritionDays,
  useUpsertNutritionDay,
} from '../../api/health';
import styles from './health.module.css';

export function HealthNutritionScreen() {
  const target = useNutritionTarget();
  const createTarget = useCreateNutritionTarget();
  const days = useNutritionDays();
  const upsertDay = useUpsertNutritionDay();

  const todayDate = new Date().toISOString().slice(0, 10);
  const todayRow = days.data?.days?.find((d) => d.date === todayDate);

  const [showTargetForm, setShowTargetForm] = useState(false);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Nutrition</h2>
          <div className={styles.subtitle}>
            Targets + day summaries. Detailed meal logging stays in MyFitnessPal
            (or your tool of choice); K-OS stores the numbers and the strategy.
          </div>
        </div>
      </header>

      {/* Target card */}
      <section className={styles.card} style={{ marginBottom: 18 }}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>Current target</h3>
          <button
            type="button"
            className="kos-btn"
            onClick={() => setShowTargetForm((s) => !s)}
          >
            {showTargetForm ? 'Cancel' : target.data ? 'Update target' : 'Set target'}
          </button>
        </div>
        {target.data && !showTargetForm ? (
          <div className={styles.kpiRow}>
            <Kpi label="kcal" value={target.data.kcal} />
            <Kpi label="protein" value={target.data.proteinG} unit="g" />
            <Kpi label="carbs" value={target.data.carbsG} unit="g" />
            <Kpi label="fat" value={target.data.fatG} unit="g" />
            <Kpi label="fiber" value={target.data.fiberG} unit="g" />
            <Kpi label="water" value={target.data.hydrationL} unit="L" />
          </div>
        ) : (
          <NutritionTargetForm
            initial={target.data ?? null}
            onSubmit={(input) =>
              createTarget.mutate(input, { onSuccess: () => setShowTargetForm(false) })
            }
            pending={createTarget.isPending}
          />
        )}
        {target.data?.strategy && !showTargetForm && (
          <div className={styles.helpText} style={{ marginTop: 8 }}>
            {target.data.strategy}
          </div>
        )}
      </section>

      {/* Today */}
      <section className={styles.card} style={{ marginBottom: 18 }}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>Today ({todayDate})</h3>
        </div>
        <DayForm
          initial={todayRow ?? null}
          onSubmit={(patch) => upsertDay.mutate({ date: todayDate, patch })}
          pending={upsertDay.isPending}
        />
      </section>

      {/* History */}
      <section style={{ marginTop: 4 }}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>Recent days</h3>
        </div>
        {(days.data?.days ?? []).length === 0 ? (
          <div className={styles.empty}>No days logged yet.</div>
        ) : (
          <div className={styles.list}>
            {(days.data?.days ?? []).slice(0, 14).map((d) => (
              <div key={d.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <div className={styles.rowTitle}>{d.date}</div>
                  <div className={styles.rowMeta}>
                    {d.kcal ?? '—'} kcal · P {d.proteinG ?? '—'} · C {d.carbsG ?? '—'} ·
                    F {d.fatG ?? '—'}
                  </div>
                </div>
                <span className={styles.tag}>{d.source}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value, unit }: { label: string; value: number | null; unit?: string }) {
  return (
    <div className={styles.kpi}>
      <div className={styles.kpiLabel}>{label}</div>
      <div>
        <span className={styles.bigNum}>{value ?? '—'}</span>
        {unit && <span className={styles.bigNumUnit}>{unit}</span>}
      </div>
    </div>
  );
}

function NutritionTargetForm({
  initial,
  onSubmit,
  pending,
}: {
  initial:
    | {
        kcal: number | null;
        proteinG: number | null;
        carbsG: number | null;
        fatG: number | null;
        fiberG: number | null;
        hydrationL: number | null;
        strategy: string;
      }
    | null;
  onSubmit: (input: Record<string, unknown>) => void;
  pending: boolean;
}) {
  const [form, setForm] = useState({
    kcal: initial?.kcal?.toString() ?? '',
    proteinG: initial?.proteinG?.toString() ?? '',
    carbsG: initial?.carbsG?.toString() ?? '',
    fatG: initial?.fatG?.toString() ?? '',
    fiberG: initial?.fiberG?.toString() ?? '',
    hydrationL: initial?.hydrationL?.toString() ?? '',
    strategy: initial?.strategy ?? '',
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      kcal: toNum(form.kcal),
      proteinG: toNum(form.proteinG),
      carbsG: toNum(form.carbsG),
      fatG: toNum(form.fatG),
      fiberG: toNum(form.fiberG),
      hydrationL: toNum(form.hydrationL),
      strategy: form.strategy,
    });
  }
  return (
    <form onSubmit={submit}>
      <div className={styles.formGrid}>
        {(
          [
            ['kcal', 'kcal'],
            ['proteinG', 'Protein (g)'],
            ['carbsG', 'Carbs (g)'],
            ['fatG', 'Fat (g)'],
            ['fiberG', 'Fiber (g)'],
            ['hydrationL', 'Water (L)'],
          ] as const
        ).map(([key, label]) => (
          <FieldNum
            key={key}
            label={label}
            value={form[key]}
            onChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
          />
        ))}
        <label className={styles.label}>Strategy</label>
        <textarea
          className={styles.textarea}
          value={form.strategy}
          onChange={(e) => setForm((f) => ({ ...f, strategy: e.target.value }))}
          placeholder="Moderate deficit + protein floor, etc."
        />
      </div>
      <div className={styles.formFoot}>
        <button type="submit" className="kos-btn kos-btn-primary" disabled={pending}>
          {pending ? 'Saving…' : 'Save target'}
        </button>
      </div>
    </form>
  );
}

function DayForm({
  initial,
  onSubmit,
  pending,
}: {
  initial:
    | {
        kcal: number | null;
        proteinG: number | null;
        carbsG: number | null;
        fatG: number | null;
        fiberG: number | null;
        hydrationL: number | null;
        notes: string;
      }
    | null;
  onSubmit: (patch: Record<string, unknown>) => void;
  pending: boolean;
}) {
  const [form, setForm] = useState({
    kcal: initial?.kcal?.toString() ?? '',
    proteinG: initial?.proteinG?.toString() ?? '',
    carbsG: initial?.carbsG?.toString() ?? '',
    fatG: initial?.fatG?.toString() ?? '',
    fiberG: initial?.fiberG?.toString() ?? '',
    hydrationL: initial?.hydrationL?.toString() ?? '',
    notes: initial?.notes ?? '',
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      kcal: toNum(form.kcal),
      proteinG: toNum(form.proteinG),
      carbsG: toNum(form.carbsG),
      fatG: toNum(form.fatG),
      fiberG: toNum(form.fiberG),
      hydrationL: toNum(form.hydrationL),
      notes: form.notes,
    });
  }
  return (
    <form onSubmit={submit}>
      <div className={styles.formGrid}>
        {(
          [
            ['kcal', 'kcal'],
            ['proteinG', 'Protein (g)'],
            ['carbsG', 'Carbs (g)'],
            ['fatG', 'Fat (g)'],
            ['fiberG', 'Fiber (g)'],
            ['hydrationL', 'Water (L)'],
          ] as const
        ).map(([key, label]) => (
          <FieldNum
            key={key}
            label={label}
            value={form[key]}
            onChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
          />
        ))}
        <label className={styles.label}>Notes</label>
        <input
          className={styles.input}
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
        />
      </div>
      <div className={styles.formFoot}>
        <button type="submit" className="kos-btn kos-btn-primary" disabled={pending}>
          {pending ? 'Saving…' : 'Save day'}
        </button>
      </div>
    </form>
  );
}

function FieldNum({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <>
      <label className={styles.label}>{label}</label>
      <input
        className={styles.input}
        type="number"
        min="0"
        step="0.1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </>
  );
}

function toNum(s: string): number | null {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
